// src/components/Menu/MenuDesktop.tsx

import React from 'react';
import styled from 'styled-components';
import { HEADER_HEIGHT } from '@/constants/layout';

import ToggleThemeButton from '@/components/ToggleThemeButton/ToggleThemeButton';
import SocialMediaMenu from '@/components/SocialMediaMenu/SocialMediaMenu';
import Logo from '@/components/Logo/Logo';
import { MenuButton } from '@/components/StyledComponents/Buttons';
import ToggleLanguageButton from '@/components/ToggleLanguageButton/ToggleLanguageButton';

const THEME_LOGO_SRC = '/images/logo/logo1-96.webp';
const THEME_LOGO_FILTER =
  'sepia(1) saturate(1.9) hue-rotate(350deg) brightness(1.08) contrast(1.22) drop-shadow(0 1px 0 rgba(255, 255, 255, 0.72)) drop-shadow(0 6px 10px rgba(0, 0, 0, 0.64))';
type MenuVariant = 'default' | 'planning';

/* ------------------------------------------------------------------ */
/* Styled — ORIGINAL (unchanged) */
/* ------------------------------------------------------------------ */

const MediaQuery = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

/**
 * ✅ FIX:
 * - Switch header layout from absolute-center to CSS Grid (3 columns)
 * - Center (logo) is ALWAYS centered, left/right can shrink without overlapping it
 */
const StickyHeader = styled.div<{ $variant: MenuVariant }>`
  position: fixed;
  top: 0;
  left: 0;
  height: ${HEADER_HEIGHT}px;
  width: 100%;
  background-color: ${({ theme, $variant }) =>
    $variant === 'planning' ? 'var(--planning-header-bg)' : theme.menuColor};
  border-bottom: ${({ $variant }) =>
    $variant === 'planning' ? '1px solid var(--planning-header-border)' : '0'};
  backdrop-filter: ${({ $variant }) =>
    $variant === 'planning' ? 'blur(14px)' : 'none'};
  z-index: 1000;

  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;

  padding: 10px 20px;
  box-sizing: border-box;

  box-shadow: ${({ $variant }) =>
    $variant === 'planning'
      ? 'var(--planning-header-shadow)'
      : '0 2px 5px rgba(0, 0, 0, 0.2)'};

  /* tighter padding as viewport approaches tablet */
  @media (max-width: 1120px) {
    padding: 8px 12px;
  }

  @media (max-width: 920px) {
    padding: 6px 8px;
  }
`;

const SideContainer = styled.div`
  display: flex;
  align-items: center;
  min-width: 0; /* 🔒 allow content to shrink inside grid */
`;

const LeftContainer = styled(SideContainer)<{ $variant: MenuVariant }>`
  justify-content: flex-start;
  justify-self: start;

  /* IMPORTANT: do not let buttons push into the center */
  overflow-x: ${({ $variant }) =>
    $variant === 'planning' ? 'auto' : 'hidden'};
  overflow-y: hidden;
  overscroll-behavior-inline: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  /* default gap for large desktop */
  gap: 6px;

  /* ✅ reduce spacing when screen gets smaller (desktop->tablet zone) */
  @media (max-width: 1120px) {
    gap: 2px;
  }

  /* on large desktop, you can even keep no gap because "|" separator exists */
  @media (min-width: 1024px) {
    gap: 0px;
  }
`;

const CenterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: center;

  /* keep your original behavior: center area itself is not clickable */
  pointer-events: none;
`;

/**
 * ✅ FIX:
 * Bring back pointer events ONLY for the logo/link itself
 * so the Link can be clicked while the container stays "transparent".
 */
const CenterClickable = styled.div`
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 999px;
  transition:
    transform 0.25s ease,
    filter 0.25s ease;

  &::after {
    content: '';
    position: absolute;
    inset: -8px -14px;
    border-radius: inherit;
    opacity: 0;
    pointer-events: none;
    background: radial-gradient(
      ellipse at center,
      rgba(255, 255, 255, 0.22) 0%,
      rgba(255, 255, 255, 0.1) 40%,
      rgba(255, 255, 255, 0) 72%
    );
    transform: scale(0.92);
    transition:
      opacity 0.25s ease,
      transform 0.25s ease;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-1px) scale(1.04);
      filter: drop-shadow(0 5px 12px rgba(0, 0, 0, 0.28));
    }

    &:hover::after {
      opacity: 1;
      transform: scale(1);
    }
  }

  @media (min-width: 769px) and (max-width: 1120px) {
    img {
      width: 168px;
    }
  }

  @media (min-width: 769px) and (max-width: 920px) {
    img {
      width: 142px;
    }
  }
`;

const RightContainer = styled(SideContainer)`
  justify-content: flex-end;
  justify-self: end;

  gap: 14px;

  /* ✅ reduce spacing when screen gets smaller */
  @media (max-width: 1120px) {
    gap: 6px;
  }

  @media (max-width: 920px) {
    gap: 4px;
  }

  /* 🔒 enforce perfect vertical alignment */
  align-items: center;

  /* do not let right-side items push into center */
  overflow: hidden;
`;

const SocialsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ToggleWrap = styled.div<{ $variant: MenuVariant }>`
  display: flex;
  align-items: center;
  height: 100%;
  line-height: 1;

  ${({ $variant }) =>
    $variant === 'planning'
      ? `
      & > div,
      & > button {
        background-color: var(--planning-control-bg);
        color: var(--planning-menu-ink);
        border-color: var(--planning-control-border);
      }

      & svg {
        color: var(--planning-accent-soft);
      }
    `
      : ''}
`;

const ThemeLogo = styled.img`
  height: 80px;
  width: auto;
  display: block;
  user-select: none;
  filter: ${THEME_LOGO_FILTER};

  /* ✅ smaller logo when screen is smaller so right side fits better */
  @media (max-width: 1120px) {
    height: 48px;
  }

  @media (max-width: 920px) {
    height: 40px;
  }
`;

const CompactMenuButton = styled(MenuButton)<{ $variant: MenuVariant }>`
  ${({ $variant, $isSelected }) =>
    $variant === 'planning'
      ? `
      color: ${
        $isSelected ? 'var(--planning-on-accent)' : 'var(--planning-menu-ink)'
      };
      background-color: ${
        $isSelected ? 'var(--planning-accent)' : 'var(--planning-panel-strong)'
      };
      border: 1px solid var(--planning-border);

      &:hover,
      &:focus-visible {
        color: var(--planning-button-hover-ink);
        background-color: var(--planning-button-hover-bg);
        outline: 2px solid var(--planning-border-strong);
        outline-offset: 3px;
      }
    `
      : ''}

  html:not(.rtl) & {
    font-size: 15px;
    padding-inline: 6px;
  }

  @media (min-width: 1121px) and (max-width: 1280px) {
    font-size: 14px;
    padding: 0 8px;
    padding-inline-start: 6px;

    html:not(.rtl) & {
      font-size: 13.5px;
      padding-inline: 5px;
    }
  }

  @media (min-width: 769px) and (max-width: 1120px) {
    height: 32px;
    font-size: 12px;
    padding: 0 6px;
    padding-inline-start: 4px;

    html:not(.rtl) & {
      font-size: 11.8px;
      padding-inline: 4px;
    }
  }

  @media (min-width: 769px) and (max-width: 920px) {
    height: 30px;
    font-size: 11px;
    padding: 0 4px;
    padding-inline-start: 3px;

    html:not(.rtl) & {
      font-size: 11px;
      padding-inline: 3px;
    }
  }

  @media (min-width: 769px) and (max-width: 840px) {
    font-size: 10.5px;
    padding: 0 3px;
    padding-inline-start: 2px;

    html:not(.rtl) & {
      font-size: 10.5px;
      padding-inline: 2px;
    }
  }

  ${({ $variant, $isSelected }) =>
    $variant === 'planning'
      ? `
      @media (min-width: 769px) {
        color: ${
          $isSelected
            ? 'var(--planning-menu-active-ink)'
            : 'var(--planning-menu-muted)'
        };
        background: ${
          $isSelected ? 'var(--planning-menu-active-bg)' : 'transparent'
        };
        border: none;
        border-inline-start: 1px solid var(--planning-border);

        &:first-child {
          border-inline-start: none;
        }

        &:hover,
        &:focus-visible {
          color: var(--planning-heading);
          background: var(--planning-menu-hover-bg);
          outline: 2px solid var(--planning-border-strong);
          outline-offset: 3px;
        }

        &::after {
          background: var(--planning-accent);
        }
      }
    `
      : ''}
`;

/* ------------------------------------------------------------------ */
/* Types */
/* ------------------------------------------------------------------ */

export type MenuDesktopProps<T extends string = string> = {
  sections: {
    id: T;
    label: string;
  }[];

  selectedSection: T;

  setSelectedSection: (section: T) => void;

  scrollToSection: (section: T) => void;

  variant?: MenuVariant;
};

/* ------------------------------------------------------------------ */
/* Component */
/* ------------------------------------------------------------------ */

const MenuDesktop = <T extends string>({
  sections,
  selectedSection,
  setSelectedSection,
  scrollToSection,
  variant = 'default',
}: MenuDesktopProps<T>) => {
  const handleClick = (id: T) => {
    setSelectedSection(id);
    scrollToSection(id);

    const hash = `#${encodeURIComponent(id)}`;
    window.history.replaceState({}, '', hash);
  };

  return (
    <MediaQuery>
      <StickyHeader
        $variant={variant}
        role="navigation"
        aria-label="תפריט ראשי (דסקטופ)"
      >
        {/* LEFT — NAV */}
        <LeftContainer $variant={variant}>
          {sections.map(s => {
            const isActive = selectedSection === s.id;

            return (
              <CompactMenuButton
                key={s.id}
                $isSelected={isActive}
                $variant={variant}
                onClick={() => handleClick(s.id)}
                aria-current={isActive ? 'page' : undefined}
              >
                {s.label}
              </CompactMenuButton>
            );
          })}
        </LeftContainer>

        {/* CENTER — LOGO */}
        <CenterContainer>
          <CenterClickable>
            <Logo decorative={false} />
          </CenterClickable>
        </CenterContainer>

        {/* RIGHT — SOCIALS + TOGGLES */}
        <RightContainer>
          <SocialsContainer>
            <SocialMediaMenu orientation="horizontal" compactDesktop />
          </SocialsContainer>

          <ToggleWrap $variant={variant}>
            <ToggleLanguageButton
              compactDesktop
              borderless
              tone={variant === 'planning' ? 'light' : 'default'}
            />
          </ToggleWrap>

          <ToggleWrap $variant={variant}>
            <ToggleThemeButton compactDesktop />
          </ToggleWrap>

          <ThemeLogo
            src={THEME_LOGO_SRC}
            alt="Logo"
            width={96}
            height={96}
            loading="lazy"
            decoding="async"
          />
        </RightContainer>
      </StickyHeader>
    </MediaQuery>
  );
};

export default MenuDesktop;
